import type { TourPlacement } from './types';

export const ATTENDEE_DASHBOARD_TAB_SLUGS = [
  'events',
  'training-history',
  'certificates',
  'tcole-psr',
  'outside-training',
  'badges',
  'leaderboard',
  'secondary-employment',
] as const;

export type AttendeeDashboardTabSlug =
  (typeof ATTENDEE_DASHBOARD_TAB_SLUGS)[number];

export function attendeeTabTourAnchor(slug: AttendeeDashboardTabSlug): string {
  return `attendee-tab-${slug}`;
}

export function attendeeTabTourTarget(slug: AttendeeDashboardTabSlug): string {
  return `[data-tour="${attendeeTabTourAnchor(slug)}"]`;
}

export interface AttendeeDashboardStepSpec {
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
  placement?: TourPlacement;
  /** When set, switch to this attendee module tab before targeting panel content. */
  attendeeTabSlug?: AttendeeDashboardTabSlug;
  /** Panel cards mount only on their tab; skip element check at filter time. */
  isPanelTarget?: boolean;
}

const TAB_COPY: Record<
  AttendeeDashboardTabSlug,
  Pick<AttendeeDashboardStepSpec, 'stepName' | 'title' | 'content'>
> = {
  events: {
    stepName: 'attendee_events_tab',
    title: 'My Events',
    content: 'View registered and upcoming training events.',
  },
  'training-history': {
    stepName: 'attendee_training_history_tab',
    title: 'Training History',
    content: 'View official completion records and reportable training credit.',
  },
  certificates: {
    stepName: 'attendee_certificates_tab',
    title: 'Certificates',
    content: 'Download and review earned certificates.',
  },
  'tcole-psr': {
    stepName: 'attendee_tcole_psr_tab',
    title: 'TCOLE PSR',
    content: 'Upload your Personal Status Report to check unit, cycle, and proficiency gaps.',
  },
  'outside-training': {
    stepName: 'attendee_outside_training_tab',
    title: 'Outside Training',
    content: 'Log and track training completed outside APT.',
  },
  badges: {
    stepName: 'attendee_badges_tab',
    title: 'Badges',
    content: 'See achievement badges you have earned from training activity.',
  },
  leaderboard: {
    stepName: 'attendee_leaderboard_tab',
    title: 'Leader Board',
    content: 'See training engagement rankings when enabled.',
  },
  'secondary-employment': {
    stepName: 'attendee_secondary_employment_tab',
    title: 'Secondary Employment',
    content: 'Browse secondary employment listings and manage your applications.',
  },
};

const PANEL_SPECS: AttendeeDashboardStepSpec[] = [
  {
    stepName: 'attendee_geo_alerts',
    tourAnchor: 'attendee-training-geo-alerts',
    title: 'Training geo alerts',
    content: 'Configure location-based alerts for training opportunities near you.',
    attendeeTabSlug: 'events',
    isPanelTarget: true,
    placement: 'bottom',
  },
  {
    stepName: 'attendee_se_applications',
    tourAnchor: 'attendee-tab-se-applications',
    title: 'My applications',
    content: 'Track listings you have applied to and their status.',
    attendeeTabSlug: 'secondary-employment',
    isPanelTarget: true,
    placement: 'bottom',
  },
  {
    stepName: 'attendee_se_assignments',
    tourAnchor: 'attendee-tab-se-assignments',
    title: 'My assignments',
    content: 'Review secondary employment assignments linked to your account.',
    attendeeTabSlug: 'secondary-employment',
    isPanelTarget: true,
    placement: 'bottom',
  },
];

const ATTENDEE_TOUR_TAB_SLUGS: AttendeeDashboardTabSlug[] = [
  'events',
  'certificates',
  'outside-training',
  'badges',
  'leaderboard',
  'secondary-employment',
];

export const ATTENDEE_DASHBOARD_STEP_SPECS: readonly AttendeeDashboardStepSpec[] =
  [
    ...ATTENDEE_TOUR_TAB_SLUGS.flatMap((slug) => {
      const tabStep: AttendeeDashboardStepSpec = {
        stepName: TAB_COPY[slug].stepName,
        tourAnchor: attendeeTabTourAnchor(slug),
        title: TAB_COPY[slug].title,
        content: TAB_COPY[slug].content,
        placement: 'bottom' as const,
      };
      if (slug === 'events') {
        const geoStep = PANEL_SPECS.find((spec) => spec.stepName === 'attendee_geo_alerts');
        return geoStep ? [tabStep, geoStep] : [tabStep];
      }
      return [tabStep];
    }),
    ...PANEL_SPECS.filter((spec) => spec.stepName !== 'attendee_geo_alerts'),
  ];

export function getAttendeeDashboardStepSpec(
  stepName: string | undefined,
): AttendeeDashboardStepSpec | undefined {
  if (!stepName) {
    return undefined;
  }
  return ATTENDEE_DASHBOARD_STEP_SPECS.find((spec) => spec.stepName === stepName);
}

export function attendeeDashboardStepTarget(spec: AttendeeDashboardStepSpec): string {
  return `[data-tour="${spec.tourAnchor}"]`;
}

function anchorExists(anchor: string, root: ParentNode): boolean {
  return root.querySelector(`[data-tour="${anchor}"]`) !== null;
}

export function shouldSkipAttendeeDashboardStep(
  spec: AttendeeDashboardStepSpec,
  root: ParentNode = document,
): boolean {
  if (spec.isPanelTarget) {
    return false;
  }
  return !anchorExists(spec.tourAnchor, root);
}

export interface BuiltAttendeeDashboardStep {
  stepName: string;
  target: string;
  placement: TourPlacement;
  title: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export function buildAttendeeDashboardTourSteps(
  copyByStep: Record<string, { title?: string; content?: string }> = {},
): BuiltAttendeeDashboardStep[] {
  return ATTENDEE_DASHBOARD_STEP_SPECS.map((spec) => {
    const copy = copyByStep[spec.stepName];
    return {
      stepName: spec.stepName,
      target: attendeeDashboardStepTarget(spec),
      placement: spec.placement ?? 'bottom',
      title: copy?.title ?? spec.title,
      content: copy?.content ?? spec.content,
      metadata: spec.attendeeTabSlug
        ? { attendeeTabSlug: spec.attendeeTabSlug }
        : undefined,
    };
  });
}
